import { Component } from 'react';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import capitalize from 'lodash/capitalize';
import AgentNotesContainer from 'components/AgentNotesContainer';
import Area from 'components/Area';
import FeaturedComparableMapContainer from 'components/FeaturedComparableMapContainer';
import Optional from 'components/Optional';
import PropertyInsightsAug2020Container from 'components/PropertyInsightsAug2020';
import 'components/FeaturedComparable.scss';
import bedIcon from 'images/bed-icon.svg';
import bathIcon from 'images/bath-icon.svg';
import carIcon from 'images/car-icon.svg';
import floorIcon from 'images/floor-icon.svg';
import landIcon from 'images/land-icon.svg';
import featuredListingsMarker from 'images/featured_listings_marker.png';
import featuredRentalsMarker from 'images/featured_rentals_marker.png';
import featuredSalesMarker from 'images/featured_sales_marker.png';
import { presetDimensions } from 'components/imaging/ResizedDasWebImage';
import DataUpdateContainer from 'components/DataUpdate';
import DisclaimerMark from 'components/DisclaimerMark/DisclaimerMark';
import FeaturedComparablePriceContainer from 'components/FeaturedComparablePrice';
import { RemoveComparableDialogContainer } from 'components/RemoveComparableDialog';
import { PropertyImageGalleryContainer } from 'components/PropertyImageGallery';

export default class FeaturedComparable extends Component {
    static propTypes = {
        comparableType: PropTypes.oneOf(['sales', 'listings', 'rentals']).isRequired,
        propertyId: PropTypes.number.isRequired,
        singleLineAddress: PropTypes.string,
        bedrooms: PropTypes.number,
        bedroomsIsEdited: PropTypes.bool,
        bathrooms: PropTypes.number,
        bathroomsIsEdited: PropTypes.bool,
        carSpaces: PropTypes.number,
        carSpacesIsEdited: PropTypes.bool,
        capitalValue: PropTypes.number,
        landArea: PropTypes.number,
        landAreaIsEdited: PropTypes.bool,
        floorArea: PropTypes.number,
        floorAreaIsEdited: PropTypes.bool,
        price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        daysOnMarket: PropTypes.number,
        distance: PropTypes.number,
        date: PropTypes.string,
        yearBuilt: PropTypes.number,
        firstAdvertisementPrice: PropTypes.number,
        latestAdvertisementPrice: PropTypes.number,
        firstPublishedPrice: PropTypes.string,
        firstPublishedPriceIsEdited: PropTypes.bool,
        latestPublishedPrice: PropTypes.string,
        latestPublishedPriceIsEdited: PropTypes.bool,
        photos: PropTypes.arrayOf(PropTypes.string.isRequired),
        deletableImages: PropTypes.arrayOf(PropTypes.string.isRequired),
        selectedPhotoIndex: PropTypes.arrayOf(PropTypes.number),
        markerLabel: PropTypes.string,
        isPriceDescriptionVisible: PropTypes.bool,
        salePriceIsEdited: PropTypes.bool,
        onSelectPhoto: PropTypes.func.isRequired,
        onRemoveFeature: PropTypes.func.isRequired,
        onRemoveSelected: PropTypes.func.isRequired,
        onUpload: PropTypes.func.isRequired,
        onDelete: PropTypes.func.isRequired,
        onPriceDescriptionClick: PropTypes.func.isRequired,
        featureFlags: PropTypes.shape({}),
    };

    constructor(props) {
        super(props);
        const {selectedPhotoIndex} = props;

        this.state = {
            currentIndex: [
                selectedPhotoIndex[0],
                selectedPhotoIndex[1],
                selectedPhotoIndex[2],
            ],
        };
    }

    componentDidUpdate(prevProps) {
        if (!isEqual(this.props.selectedPhotoIndex, prevProps.selectedPhotoIndex)) {
            this.setState({
                currentIndex: [
                    this.props.selectedPhotoIndex[0],
                    this.props.selectedPhotoIndex[1],
                    this.props.selectedPhotoIndex[2],
                ],
            });
        }
    }

    onIndexChange = (galleryIdx) => (photoIdx) => {
        const currentIndex = [...this.state.currentIndex];
        const {photos, onSelectPhoto} = this.props;

        currentIndex[galleryIdx] = photoIdx;
        this.setState({currentIndex});
        onSelectPhoto(galleryIdx, photos[photoIdx]);
    };

    onRemoveFeature = () => {
        const {onRemoveFeature} = this.props;
        onRemoveFeature();
    };

    onRemoveSelected = () => {
        const {onRemoveSelected} = this.props;
        onRemoveSelected();
    };

    upload = (index) => (files) => {
        this.props.onUpload(index, files);
    };

    getPropertyInsights = () => {
        return <PropertyInsightsAug2020Container {...this.props} />;
    };

    render() {
        const {
            bedrooms,
            bedroomsIsEdited,
            bathrooms,
            bathroomsIsEdited,
            carSpaces,
            carSpacesIsEdited,
            landArea,
            landAreaIsEdited,
            floorArea,
            floorAreaIsEdited,
            price,
            photos,
            deletableImages,
            singleLineAddress,
            propertyId,
            comparableType,
            markerLabel,
            onDelete,
        } = this.props;

        if (!singleLineAddress) {
            return null;
        }

        const {currentIndex} = this.state;

        const pageContext = 'Summary';
        const objectContext = `Comparable ${capitalize(comparableType)}`;

        const comparableItemData = {
            sales: {
                label: (
                    <Typography className="text">Sold Price</Typography>
                ),
                markerIcon: featuredSalesMarker,
                component: () =>
                    (
                        <FeaturedComparablePriceContainer comparableType={comparableType}
                                                          descriptiveFieldLabel="sold price"
                                                          propertyId={propertyId}/>
                    ),
            },
            listings: {
                label: (
                    <Typography className="text">Listing Price</Typography>
                ),
                markerIcon: featuredListingsMarker,
                component: () => (
                    <FeaturedComparablePriceContainer comparableType={comparableType}
                                                      descriptiveFieldLabel="listing price"
                                                      propertyId={propertyId}/>
                ),
            },
            rentals: {
                label: <Typography className="text">Listing Price</Typography>,
                markerIcon: featuredRentalsMarker,
                component: () => <Optional value={price}/>,
            },
        }[comparableType];

        return (
            <div className="FeaturedComparable">
                <div className="header-container">
                    <div className="single-line-address">
                        <img src={comparableItemData.markerIcon} alt=""/>
                        <div className="marker-label">{markerLabel}</div>
                        <span>{singleLineAddress}</span>
                    </div>
                    <div className="header-right">
                        <div className={`sale-price ${comparableType}`}>
                            {comparableItemData.label}
                            {comparableItemData.component()}
                        </div>
                        <RemoveComparableDialogContainer onConfirmation={this.onRemoveSelected}/>
                    </div>
                </div>
                <div className="top-row">
                    <div className="attributes">
                        <div className="bedrooms attribute">
                            <img alt="bed icon" src={bedIcon}/>
                            <Optional value={bedrooms}/>
                            {bedroomsIsEdited && <DisclaimerMark/>}
                        </div>
                        <div className="bathrooms attribute">
                            <img alt="bath icon" src={bathIcon}/>
                            <Optional value={bathrooms}/>
                            {bathroomsIsEdited && <DisclaimerMark/>}
                        </div>
                        <div className="car-spaces attribute">
                            <img alt="car icon" src={carIcon}/>
                            <Optional value={carSpaces}/>
                            {carSpacesIsEdited && <DisclaimerMark/>}
                        </div>
                        <div className="land-area attribute">
                            <img alt="land area icon" src={landIcon}/>
                            <Area area={landArea}/>
                            {landAreaIsEdited && <DisclaimerMark/>}
                        </div>
                        <div className="floor-area attribute">
                            <img alt="floor area icon" src={floorIcon}/>
                            <Area area={floorArea}/>
                            {floorAreaIsEdited && <DisclaimerMark/>}
                        </div>
                    </div>
                    <DataUpdateContainer pageContext={pageContext} propertyContext={objectContext}
                                         propertyId={propertyId} comparableType={comparableType}/>
                </div>
                <div className="feature-property">
                    <FormControlLabel
                        aria-label="Feature this comparable"
                        onChange={this.onRemoveFeature}
                        onClick={(event => event.stopPropagation())}
                        onFocus={(event) => event.stopPropagation()}
                        checked
                        control={<Checkbox className="property-select" color="primary"/>}
                        label="Feature this comparable"
                    />
                </div>
                <div className="images">
                    {[0, 1, 2].map(i => (
                        <PropertyImageGalleryContainer
                            comparableType={comparableType}
                            currentIndex={currentIndex[i]}
                            deletableImages={deletableImages}
                            images={photos}
                            key={`featured-comparable-property-gallery-${i}-${propertyId}`}
                            objectContext={objectContext}
                            pageContext={pageContext}
                            propertyId={propertyId}
                            resizeProps={{isResize: true, resizeDimensions: presetDimensions.PROPERTY_FEATURED}}
                            onIndexChange={this.onIndexChange(i)}
                            onDelete={() => {
                                onDelete(currentIndex[i]);
                                this.onIndexChange(i)(i);
                            }}
                            onUpload={this.upload(i)}
                        />
                    ))}
                    <div className="notes">
                        <AgentNotesContainer propertyId={propertyId} comparableType={comparableType}
                                             hideNotesSelected={true}
                                             reportPage="feature"/>
                    </div>
                </div>
                <div className="google-maps">
                    <FeaturedComparableMapContainer propertyId={propertyId} comparableType={comparableType}
                                                    markerLabel={markerLabel}/>
                    {this.getPropertyInsights()}
                </div>
            </div>
        );
    }
}